/*
 * image-watchdog.js  (s13, 2026-07-23)
 *
 * THE BUG IT HUNTS: a product tile image occasionally sits EMPTY on the grid --
 * not a broken-image glyph, an empty box -- and a reload does NOT fix it while
 * opening a fresh window DOES. The tiles are 100% native (<img loading=lazy
 * decoding=async>, no theme JS in the path), so the misfire lives in the browser's
 * own lazy/decode/transport layer (leading theory: a native loading=lazy trigger
 * missed after a post-load reflow; CDN serves images over h3/QUIC, so a stalled
 * QUIC stream / poisoned h3 connection returns an empty or never-arriving body --
 * and a reload reuses that same connection while a fresh window opens a new one).
 *
 *   1. LOG it  -- turn the Heisenbug into data (one decisive Umami/console beacon).
 *   2. HEAL it -- recover the stuck tile regardless of root cause.
 *
 * ---------------------------------------------------------------------------------
 * s13 CHANGELOG -- why s12's heal did not fire in production (proven in a repro harness
 * running this exact file against deliberately-broken image endpoints, 2026-07-23):
 *
 *   (A) SILENT HANG was the killer. The production failure is a *stalled* transport:
 *       the <img> request opens but the body never arrives. A hung <img> NEVER fires
 *       an `error` event -- so s12's heal, which armed only load/error listeners and
 *       waited, sat there forever. Empty tile, no recovery, and (because 'detected'
 *       already spent a beacon) often no terminal telemetry either. s13 arms its OWN
 *       per-attempt TIMEOUT, so a hang is caught and retried instead of hanging mute.
 *
 *   (B) ONE IMMEDIATE SAME-PATH RETRY is too weak for a transient QUIC stall. s12
 *       cache-busted once, immediately -- riding the same poisoned connection, so the
 *       retry stalled/emptied identically. s13 does N spaced retries (backoff), which
 *       gives the browser time to migrate/re-establish the h3 connection -- the same
 *       thing "open a fresh window" does by hand.
 *
 *   (C) LOAD != PAINT. An empty-body 200 (QUIC truncation) can fire `load` with
 *       naturalWidth 0. s12 would have treated that as success. s13 verifies
 *       naturalWidth>0 after every load and retries if the tile is still blank.
 *
 *   (D) BEACON STARVATION. s12's MAX_BEACONS=6 was exhausted by ~2 failing tiles
 *       (each incident spends detected + a retry error + terminal), so later tiles'
 *       'unhealed' beacons were dropped -- the exact tiles you most want to see.
 *       s13 raises the cap and each incident is guaranteed its terminal beacon.
 *
 *   (E) SLOW vs STUCK -- deliberately NOT distinguished. There is no reliable
 *       "bytes are in flight" signal from JS (getEntriesByType('resource') lists only
 *       COMPLETED fetches, never in-progress ones), so a tile unpainted 5s after it is
 *       in view is treated as stuck and healed. A merely-slow tile may thus be
 *       force-healed -- harmless: it still paints; the only cost is trading that one
 *       tile's responsive srcset for a concrete src. 5s in-view is already very generous
 *       for a few-KB CDN render, so real slow-but-fine hits are rare.
 *
 * KNOWN LIMIT (documented, not a bug): the heal recovers via the <img> element (no
 * cross-origin fetch), because Shopify CDN renders are cross-origin and an opaque
 * fetch can't be read into a blob. So heal cannot *force* a brand-new h3 connection
 * from JS; it relies on spaced retries landing on a healthy connection. For a
 * persistently poisoned connection within one page life the terminal 'unhealed'
 * beacon fires so the incidence is still measured. Root cause (CDN/QUIC) is external;
 * escalate via the RUM dashboard if 'unhealed' rate climbs.
 *
 * COST: still effectively zero on a healthy page -- passive IntersectionObserver, a
 * single per-tile grace timer cleared the instant the image loads, no polling/rAF.
 * FAIL-OPEN: whole thing wrapped so it can never break the page.
 */
(function () {
  "use strict";
  try {
    var SELECTOR = ".home-tile-img img, .collection-tile-img img";
    var GRACE_MS = 5000;        // after a tile is near-viewport, how long before we call it stuck
    var ROOT_MARGIN = "300px";  // start watching a bit before it scrolls into view
    var HEAL_ATTEMPTS = 3;      // total re-fetch attempts before giving up
    var HEAL_TIMEOUT_MS = 4000; // per-attempt ceiling -> a HANG becomes a caught failure, not a mute stall
    var HEAL_BACKOFF_MS = 800;  // spacing between attempts (x attempt#) -> chance at a fresh h3 connection
    var MAX_BEACONS = 20;       // bounded telemetry; ~2 beacons/incident => ~10 incidents before we go quiet
    var beacons = 0;

    function painted(img) { return img.complete && img.naturalWidth > 0; }

    function navType() {
      try {
        var n = performance.getEntriesByType("navigation")[0];
        return n ? n.type : "unknown";           // 'reload' | 'navigate' | 'back_forward'
      } catch (e) { return "unknown"; }
    }

    function resourceEntry(url) {
      try {
        var list = performance.getEntriesByType("resource");
        for (var i = list.length - 1; i >= 0; i--) if (list[i].name === url) return list[i];
      } catch (e) {}
      return null;
    }

    function beacon(img, phase, extra) {
      if (beacons >= MAX_BEACONS) return;
      beacons++;
      var url = img.currentSrc || img.src || "";
      var e = resourceEntry(url);
      var a = img.closest ? img.closest("a") : null;
      var data = {
        phase: phase,                                   // 'detected' | 'healed' | 'unhealed'
        page: location.pathname,
        product: a ? a.getAttribute("href") : null,
        chosenWidth: (url.split("width=")[1] || "").split("&")[0] || null,
        fetched: e ? true : false,                      // best-effort: did a request record exist?
        proto: e ? e.nextHopProtocol : null,            // h2 / h3 / ...
        durMs: e ? Math.round(e.duration) : null,
        transfer: e ? e.transferSize : null,            // 0 with an entry => failed/empty body
        nav: navType(),                                 // reload vs fresh window is the whole clue
        loading: img.getAttribute("loading"),
        attempts: extra && extra.attempts != null ? extra.attempts : null,
        reason: extra && extra.reason || null           // why the last attempt failed: timeout|error|blank
      };
      try { console.warn("[img-watchdog]", phase, data); } catch (_) {}
      try { if (window.umami && typeof window.umami.track === "function") window.umami.track("img_misfire", data); } catch (_) {}
    }

    // Strip any watchdog cache-bust params so retries build cleanly off the original URL.
    function baseUrl(u) { return u ? u.replace(/([?&])wd\d?=[^&]*/g, "$1").replace(/[?&]$/, "") : u; }
    function bust(u, tok) { return u + (u.indexOf("?") > -1 ? "&" : "?") + "wd=" + tok; }

    function heal(img) {
      var candidate = img.currentSrc || img.getAttribute("src");
      if (!candidate) { beacon(img, "unhealed", { attempts: 0, reason: "no-src" }); return; }
      var base = baseUrl(candidate);
      var attempt = 0;
      var strippedResponsive = false;

      function tryOnce() {
        attempt++;
        var settled = false;
        function clear() {
          settled = true;
          clearTimeout(to);
          img.removeEventListener("load", onLoad);
          img.removeEventListener("error", onErr);
        }
        function onLoad() {
          if (settled) return;
          if (img.naturalWidth > 0) { clear(); beacon(img, "healed", { attempts: attempt }); }
          else { clear(); next("blank"); }        // load fired but tile is blank (empty body) -> retry
        }
        function onErr() { if (settled) return; clear(); next("error"); }
        var to = setTimeout(function () {          // (A) our own timeout: catches the SILENT HANG
          if (settled) return; clear(); next("timeout");
        }, HEAL_TIMEOUT_MS);

        img.addEventListener("load", onLoad);
        img.addEventListener("error", onErr);

        // First attempt: eager + drop srcset/sizes so the src we set is the one that fetches.
        if (!strippedResponsive) {
          strippedResponsive = true;
          try { img.loading = "eager"; } catch (_) {}
          try { img.removeAttribute("srcset"); img.removeAttribute("sizes"); } catch (_) {}
        }
        img.setAttribute("src", bust(base, Date.now() + "-" + attempt));  // fresh URL every attempt
      }

      function next(reason) {
        if (attempt >= HEAL_ATTEMPTS) { beacon(img, "unhealed", { attempts: attempt, reason: reason }); return; }
        setTimeout(tryOnce, HEAL_BACKOFF_MS * attempt);  // (B) spaced retry
      }

      tryOnce();
    }

    function watch(img) {
      if (painted(img)) return;
      var timer = setTimeout(function () {
        if (painted(img)) return;                        // loaded during grace -> all good
        beacon(img, "detected");
        heal(img);
      }, GRACE_MS);
      img.addEventListener("load", function () { clearTimeout(timer); }, { once: true });
    }

    function start() {
      try {
        var imgs = document.querySelectorAll(SELECTOR);
        if (!imgs.length) return;
        window.__imgWatchdog = { version: "s13", watched: 0, beacons: function () { return beacons; } };
        if (!("IntersectionObserver" in window)) {
          for (var i = 0; i < imgs.length; i++) watch(imgs[i]);
          window.__imgWatchdog.watched = imgs.length;
          return;
        }
        var io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { obs.unobserve(en.target); watch(en.target); }
          });
        }, { rootMargin: ROOT_MARGIN });
        var n = 0;
        imgs.forEach(function (img) { if (!painted(img)) { io.observe(img); n++; } });
        window.__imgWatchdog.watched = n;
      } catch (e) { try { console.warn("[img-watchdog] start failed", e); } catch (_) {} }
    }

    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
    else start();
  } catch (e) { /* fail-open: never break the page */ }
})();
